import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Barcode, CheckCircle2, ChevronLeft, Keyboard, X } from 'lucide-react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { ConfrimModal } from '../../components/ConfrimModal';
import { useReceivingStore } from './ReceivingStore';
import { canCloseReceipt, useTouchLayout, useVisualViewportHeight } from './receivingLayout';
import { detectEpcCounts, extractUniqueEpcTags } from './receivedUtils';
import { SlimBoxProgress } from './receivingAtoms';
import {
  bulkDraftKey,
  bulkRowAction,
  flattenReceivingBoxes,
  overExpectedBulkRows,
  rowEnteredUnits,
  summarizeBulkRows,
} from './bulkReceiveModel';

const CONCURRENCY = 4;

function readDraft(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeDraft(key, rows) {
  try {
    window.localStorage.setItem(key, JSON.stringify(rows));
  } catch (error) {
    // Draft persistence is best effort.
  }
}

function BulkReceiveWorkspace({ selectedPoIds, onClose }) {
  const touchLayout = useTouchLayout();
  const vvHeight = useVisualViewportHeight();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const canClose = canCloseReceipt(currentUser);
  const {
    ensurePoLoaded,
    captureScan,
    manualReceiveBox,
    overrideReceiveBox,
    closeReceipt,
  } = useReceivingStore();
  const selectionKey = useMemo(
    () => [...selectedPoIds].map(String).sort().join(','),
    [selectedPoIds],
  );
  const selectedIds = useMemo(
    () => (selectionKey ? selectionKey.split(',') : []),
    [selectionKey],
  );
  const draftKey = useMemo(() => bulkDraftKey(selectedIds), [selectedIds]);
  const [loadedPos, setLoadedPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('scan');
  const [scanKeyboard, setScanKeyboard] = useState(false);
  const [rows, setRows] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [capture, setCapture] = useState('');
  const [applyState, setApplyState] = useState(null);
  const [closeFullyReceived, setCloseFullyReceived] = useState(false);
  const [overrideCompleted, setOverrideCompleted] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [scanValidation, setScanValidation] = useState(null);
  const captureRef = useRef(null);
  const manualInputRefs = useRef({});
  const rowRefs = useRef({});
  const discardedRef = useRef(false);
  const backdropPointerDownRef = useRef(false);

  const summary = useMemo(() => summarizeBulkRows(rows), [rows]);
  const activeRow = rows[activeIndex];
  const hasUnsavedEntries = rows.some(
    (row) => rowEnteredUnits(row) > 0 && row.status !== 'success',
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(selectedIds.map((id) => ensurePoLoaded(id)))
      .then((loaded) => {
        if (cancelled) return;
        const nextPos = loaded.filter(Boolean);
        setLoadedPos(nextPos);
        const baseRows = flattenReceivingBoxes(nextPos);
        const draft = readDraft(draftKey);
        if (draft && Array.isArray(draft)) {
          const byId = new Map(draft.map((row) => [row.id, row]));
          setRows(
            baseRows.map((row) => ({
              ...row,
              ...(byId.get(row.id) || {}),
              inputQty: byId.get(row.id)?.inputQty ?? '',
              epcs: byId.get(row.id)?.epcs || [],
              status: byId.get(row.id)?.status || 'pending',
            })),
          );
        } else {
          setRows(baseRows.map((row) => ({ ...row, inputQty: '', epcs: [], status: 'pending' })));
        }
      })
      .catch(() => toast.error('Failed to load selected POs', { position: 'bottom-right' }))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftKey, ensurePoLoaded, selectedIds]);

  useEffect(() => {
    if (!loading && !discardedRef.current) writeDraft(draftKey, rows);
  }, [draftKey, loading, rows]);

  const updateRow = useCallback((index, patch) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  const advance = useCallback((index) => {
    setActiveIndex(Math.min(rows.length - 1, index + 1));
  }, [rows.length]);

  const commitScan = useCallback(({ allowDuplicates = true } = {}) => {
    if (!activeRow) return;
    if (
      activeRow.existingUnits >= activeRow.expectedUnits &&
      activeRow.expectedUnits > 0 &&
      !overrideCompleted
    ) {
      advance(activeIndex);
      return;
    }
    const epcs = extractUniqueEpcTags(capture);
    if (!epcs.length) return;
    if (scanValidation?.rowId === activeRow.id && scanValidation.isValidating) {
      return;
    }
    const duplicates =
      scanValidation?.rowId === activeRow.id ? scanValidation.duplicates || [] : [];
    if (duplicates.length && !allowDuplicates) {
      return;
    }
    const validEpcs =
      scanValidation?.rowId === activeRow.id && scanValidation.validEpcs?.length
        ? scanValidation.validEpcs
        : epcs;
    const over = validEpcs.length > activeRow.expectedUnits;
    updateRow(activeIndex, {
      epcs: validEpcs,
      duplicates,
      inputQty: '',
      status: over ? 'over' : 'entered',
    });
    setCapture('');
    setScanValidation(null);
    advance(activeIndex);
  }, [
    activeIndex,
    activeRow,
    advance,
    capture,
    overrideCompleted,
    scanValidation,
    updateRow,
  ]);

  useEffect(() => {
    if (
      mode === 'scan' &&
      activeRow &&
      activeRow.expectedUnits > 0 &&
      scanValidation?.rowId === activeRow.id &&
      !scanValidation.isValidating &&
      extractUniqueEpcTags(capture).length >= activeRow.expectedUnits
    ) {
      commitScan({ allowDuplicates: false });
    }
  }, [activeRow, capture, commitScan, mode, scanValidation]);

  const commitManual = useCallback((index) => {
    const row = rows[index];
    if (!row || row.inputQty === '' || row.inputQty == null) return;
    const quantity = Number(row.inputQty);
    if (!Number.isFinite(quantity) || quantity < 0) return;
    updateRow(index, {
      inputQty: String(quantity),
      epcs: [],
      status: quantity > row.expectedUnits ? 'over' : 'entered',
    });
    advance(index);
  }, [advance, rows, updateRow]);

  const fillExpected = () => {
    const emptyRows = rows.filter(
      (row) => rowEnteredUnits(row) === 0 && row.existingUnits < row.expectedUnits,
    );
    if (!emptyRows.length) {
      toast.info('No empty boxes to fill', { position: 'bottom-right' });
      return;
    }
    setRows((current) =>
      current.map((row) =>
        rowEnteredUnits(row) > 0 || row.existingUnits >= row.expectedUnits
          ? row
          : { ...row, inputQty: String(row.expectedUnits), status: 'entered' },
      ),
    );
    toast.success(`Filled ${emptyRows.length} boxes`, { position: 'bottom-right' });
  };

  const fillDown = () => {
    const source = rows[activeIndex];
    if (!source) return;
    const quantity = rowEnteredUnits(source);
    if (!quantity) {
      toast.info('Enter a value in the highlighted row first', { position: 'bottom-right' });
      return;
    }
    const emptyRows = rows.filter(
      (row, index) => index >= activeIndex && rowEnteredUnits(row) === 0,
    );
    if (!emptyRows.length) {
      toast.info('No empty boxes below the highlighted row', { position: 'bottom-right' });
      return;
    }
    setRows((current) =>
      current.map((row, index) =>
        index >= activeIndex && rowEnteredUnits(row) === 0
          ? { ...row, inputQty: String(quantity), status: 'entered' }
          : row,
      ),
    );
    toast.success(`Filled ${emptyRows.length} boxes`, { position: 'bottom-right' });
  };

  const applyRows = async (allowOverReceipt = false) => {
    const pending = rows.filter((row) => rowEnteredUnits(row) > 0 && row.status !== 'success');
    const overRows = overExpectedBulkRows(pending);
    if (overRows.length && !allowOverReceipt) {
      setConfirmState({ kind: 'apply', overRows });
      return;
    }
    setApplyState({ total: pending.length, completed: 0, failed: 0, running: true });
    let cursor = 0;
    const failures = [];
    const worker = async () => {
      while (cursor < pending.length) {
        const row = pending[cursor];
        cursor += 1;
        try {
          const action = bulkRowAction(row);
          const quantity = rowEnteredUnits(row);
          if (action === 'captureScan') {
            await captureScan(row.poId, row.boxId, {
              epcs: row.epcs,
              matchedCount: quantity,
              totalScanned: quantity,
              poMatched: quantity,
              duplicates: row.duplicates || [],
            }, 'initial');
          } else if (action === 'overrideReceiveBox') {
            await overrideReceiveBox(row.poId, row.boxId, quantity);
          } else {
            await manualReceiveBox(row.poId, row.boxId, quantity);
          }
          setRows((current) => current.map((item) => item.id === row.id ? { ...item, status: 'success' } : item));
          setApplyState((current) => ({ ...current, completed: current.completed + 1 }));
        } catch (error) {
          failures.push(row.id);
          setRows((current) => current.map((item) => item.id === row.id ? { ...item, status: 'failed', error: error?.message || 'Failed' } : item));
          setApplyState((current) => ({ ...current, failed: current.failed + 1 }));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker));
    setApplyState((current) => ({ ...current, running: false, failures }));
    if (!failures.length) {
      try {
        window.localStorage.removeItem(draftKey);
      } catch (error) {
        // Ignore unavailable storage.
      }
      const completePoIds = closeFullyReceived && canClose
        ? loadedPos.filter((po) => rows
          .filter((row) => row.poId === po.id)
          .every((row) => row.existingUnits >= row.expectedUnits || rowEnteredUnits(row) >= row.expectedUnits))
        .map((po) => po.id)
        : [];
      if (completePoIds.length) await closeReceipt(completePoIds);
      toast.success('Bulk receiving applied successfully', { position: 'bottom-right' });
    } else {
      toast.error(`${failures.length} row(s) failed. Retry failed to continue.`, { position: 'bottom-right' });
    }
  };

  const retryFailed = () => {
    setRows((current) => current.map((row) => row.status === 'failed' ? { ...row, status: 'pending' } : row));
    setApplyState(null);
  };

  const handleClose = useCallback(() => {
    if (applyState?.running) return;
    if (hasUnsavedEntries) {
      setConfirmState({ kind: 'discard' });
      return;
    }
    onClose();
  }, [applyState?.running, hasUnsavedEntries, onClose]);

  useEffect(() => {
    if (mode !== 'scan' || !activeRow || !capture) {
      setScanValidation(null);
      return undefined;
    }
    const uniqueTags = extractUniqueEpcTags(capture);
    if (!uniqueTags.length || !activeRow.poDbId) {
      setScanValidation(null);
      return undefined;
    }

    setScanValidation({
      rowId: activeRow.id,
      isValidating: true,
      validEpcs: [],
      duplicates: [],
    });
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const { data } = await axios.post('/api/v1/po-received-logs/validate-epcs', {
          po_id: activeRow.poDbId,
          epc_hex_tags: uniqueTags,
        });
        if (cancelled) return;
        const byProduct = data.byProduct || [];
        const row = activeRow.productId
          ? byProduct.find((product) => String(product.product_id) === String(activeRow.productId))
          : byProduct.find((product) => product.sku === activeRow.sku);
        setScanValidation({
          rowId: activeRow.id,
          isValidating: false,
          validEpcs: row?.validEpcs || [],
          duplicates: data.duplicates || [],
        });
      } catch (error) {
        if (!cancelled) {
          setScanValidation({
            rowId: activeRow.id,
            isValidating: false,
            validEpcs: [],
            duplicates: [],
          });
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [activeRow, capture, mode]);

  useEffect(() => {
    if (mode === 'scan' && !applyState) captureRef.current?.focus();
  }, [activeIndex, applyState, mode]);

  useEffect(() => {
    if (mode === 'manual' && !applyState) {
      const input = manualInputRefs.current[activeIndex];
      input?.focus();
      input?.select();
    }
  }, [activeIndex, applyState, mode]);

  useEffect(() => {
    if (
      !loading &&
      activeRow &&
      activeRow.existingUnits >= activeRow.expectedUnits &&
      activeRow.expectedUnits > 0 &&
      !overrideCompleted &&
      activeIndex < rows.length - 1
    ) {
      setActiveIndex((index) => index + 1);
    }
  }, [activeIndex, activeRow, loading, overrideCompleted, rows.length]);

  useEffect(() => {
    rowRefs.current[activeRow?.id]?.scrollIntoView({ block: 'nearest' });
  }, [activeRow?.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  const groupedRows = rows.reduce((groups, row, index) => {
    const key = row.poId || row.poNumber;
    let group = groups.find((item) => item.key === key);
    if (!group) {
      group = {
        key,
        poNumber: row.poNumber,
        supplier: row.supplier,
        rows: [],
        expectedUnits: 0,
        enteredUnits: 0,
      };
      groups.push(group);
    }
    group.rows.push({ row, index });
    group.expectedUnits += row.expectedUnits;
    group.enteredUnits += rowEnteredUnits(row);
    return groups;
  }, []);
  const summaryProgressBoxes = rows.map((row) => ({
    expectedUnits: row.expectedUnits,
    receivedUnits: rowEnteredUnits(row),
    state: row.status,
  }));

  return createPortal(
    (
    <div
      className={`fixed z-[120] flex bg-gray-900/35 text-gray-900 ${
        touchLayout
          ? 'inset-x-0 items-stretch justify-stretch p-0'
          : 'inset-0 items-center justify-center p-3 sm:p-6'
      }`}
      style={
        touchLayout
          ? {
              height: vvHeight ? `${vvHeight}px` : '100dvh',
              top: window.visualViewport?.offsetTop ?? 0,
            }
          : undefined
      }
      onMouseDown={(event) => {
        backdropPointerDownRef.current = false;
        if (event.target === event.currentTarget) {
          backdropPointerDownRef.current = true;
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && backdropPointerDownRef.current) {
          backdropPointerDownRef.current = false;
          handleClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Bulk receive"
        className={`flex w-full flex-col overflow-hidden bg-gray-50 shadow-2xl ${
          touchLayout
            ? 'h-full max-h-none max-w-none rounded-none border-0'
            : 'max-h-[85vh] max-w-[1500px] rounded-xl border border-gray-300'
        }`}
        onMouseDown={(event) => {
          backdropPointerDownRef.current = false;
          event.stopPropagation();
        }}
      >
      <header className="shrink-0 border-b border-gray-200 bg-white">
        <div
          className={`flex flex-wrap items-center justify-between ${
            touchLayout ? 'gap-3 px-3 py-2' : 'gap-6 px-4 py-4 sm:px-6'
          }`}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {touchLayout && (
              <button
                type="button"
                onClick={handleClose}
                className="flex shrink-0 items-center gap-1 rounded px-1 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                <ChevronLeft className="h-5 w-5" />
                Queue
              </button>
            )}
            <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">Bulk receive</h2>
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-sm font-medium text-gray-600">
                {selectedIds.length} {selectedIds.length === 1 ? 'PO' : 'POs'}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              <SlimBoxProgress boxes={summaryProgressBoxes} className="!w-[180px]" />
              <span className="tabular-nums">
                {summary.enteredBoxes}/{summary.totalBoxes} boxes · {summary.enteredUnits}/{summary.expectedUnits} units
              </span>
              <span className={`font-medium tabular-nums ${summary.variance === 0 ? 'text-green-700' : 'text-amber-700'}`}>
                variance {summary.variance > 0 ? '+' : ''}{summary.variance}
              </span>
            </div>
          </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Close bulk receive"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </header>
      <main
        className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
          touchLayout ? 'p-2' : 'px-3 py-3 sm:px-6 sm:py-4'
        }`}
      >
        <div
          className={`mx-auto flex min-h-0 w-full flex-col gap-3 ${
            touchLayout ? 'max-w-none flex-1' : 'max-w-[1400px]'
          }`}
        >
          <section className="shrink-0 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex rounded-lg border border-gray-300 bg-gray-50 p-1">
                {['scan', 'manual'].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMode(value)}
                    className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
                      mode === value
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    {value === 'scan' ? 'Scan mode' : 'Manual mode'}
                  </button>
                ))}
              </div>
              {mode === 'manual' && (
                <div className="flex items-center gap-2 border-l border-gray-200 pl-3">
                  <button
                    type="button"
                    onClick={fillExpected}
                    title="Fill every empty box with its expected quantity"
                    disabled={!rows.some(
                      (row) => rowEnteredUnits(row) === 0 && row.existingUnits < row.expectedUnits,
                    )}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Fill expected for all
                  </button>
                  <button
                    type="button"
                    onClick={fillDown}
                    title="Copy the highlighted row's entry into each subsequent empty box"
                    disabled={!activeRow || rowEnteredUnits(activeRow) === 0}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Fill down
                  </button>
                </div>
              )}
              <div className="ml-auto flex flex-wrap items-center gap-4 border-l border-gray-200 pl-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={overrideCompleted}
                    onChange={(event) => setOverrideCompleted(event.target.checked)}
                  />
                  Include completed boxes
                </label>
                {canClose && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={closeFullyReceived}
                      onChange={(event) => setCloseFullyReceived(event.target.checked)}
                    />
                    Close fully received POs
                  </label>
                )}
              </div>
            </div>
          </section>
          {mode === 'scan' && (
            <section className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 p-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 text-sm text-blue-900">
                <Barcode className="h-5 w-5 shrink-0" />
                <input
                  ref={captureRef}
                  value={capture}
                  onChange={(event) => setCapture(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitScan();
                    }
                  }}
                  inputMode={scanKeyboard ? 'text' : 'none'}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  enterKeyHint="done"
                  placeholder="Scan EPCs for the highlighted box…"
                  className="h-10 min-w-[220px] flex-1 rounded-md border border-blue-200 bg-white px-3 font-mono text-base shadow-inner"
                />
                <button
                  type="button"
                  onClick={() => {
                    setScanKeyboard((enabled) => !enabled);
                    captureRef.current?.focus();
                  }}
                  aria-label="Type EPCs with keyboard"
                  aria-pressed={scanKeyboard}
                  className="inline-flex h-10 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-white px-2 text-blue-900 hover:bg-blue-100"
                >
                  <Keyboard className="h-5 w-5" />
                </button>
                <span className="tabular-nums">{extractUniqueEpcTags(capture).length}/{activeRow?.expectedUnits || 0} unique</span>
                {detectEpcCounts(capture)?.total > detectEpcCounts(capture)?.unique && (
                  <span className="text-amber-700">Duplicate EPC reads removed</span>
                )}
                {scanValidation && scanValidation.rowId === activeRow?.id && scanValidation.duplicates?.length > 0 && (
                  <span className="text-red-700">
                    Duplicate EPC {scanValidation.duplicates[0].hexEpc} — already received in {scanValidation.duplicates[0].locationLabel}
                  </span>
                )}
                <button
                  type="button"
                  onClick={commitScan}
                  className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                >
                  Commit &amp; advance
                </button>
              </div>
            </section>
          )}
          <section
            className={`flex min-h-0 flex-col overflow-hidden ${
              touchLayout
                ? 'flex-1 border-0 bg-white shadow-none'
                : 'rounded-lg border border-gray-200 bg-white shadow-sm'
            }`}
          >
            <div className="min-h-0 flex-1 overflow-auto">
              {loading ? (
                <div className="flex h-full min-h-[240px] items-center justify-center">
                  <LoaderSpinner height="80" width="80" colorSpinner="black" border="5" />
                </div>
              ) : (
                <table
                  className={`w-full border-collapse text-base ${
                    touchLayout ? 'min-w-0' : 'min-w-[760px]'
                  }`}
                >
                  <thead>
                    <tr className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <th className={touchLayout ? 'w-14 px-1.5 py-2.5' : 'w-20 px-3 py-2.5'}>Box</th>
                      <th className={touchLayout ? 'px-1.5 py-2.5' : 'px-3 py-2.5'}>SKU / Product</th>
                      <th className={touchLayout ? 'w-20 px-1.5 py-2.5 text-right' : 'w-28 px-3 py-2.5 text-right'}>Expected</th>
                      <th className={touchLayout ? 'w-20 px-1.5 py-2.5 text-right' : 'w-28 px-3 py-2.5 text-right'}>Received</th>
                      <th className={touchLayout ? 'w-28 px-1.5 py-2.5 text-right' : 'w-36 px-3 py-2.5 text-right'}>Entry</th>
                      <th className={touchLayout ? 'w-32 px-1.5 py-2.5' : 'w-48 px-3 py-2.5'}>State</th>
                      <th className={touchLayout ? 'w-14 px-1.5 py-2.5 text-right' : 'w-20 px-3 py-2.5 text-right'} />
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRows.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <td colSpan={7} className="px-3 py-2.5">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">PO {group.poNumber}</span>
                                <span className="text-sm text-gray-500">{group.supplier}</span>
                              </div>
                              <span className="text-sm tabular-nums text-gray-600">
                                {group.enteredUnits}/{group.expectedUnits} units
                              </span>
                            </div>
                          </td>
                        </tr>
                        {group.rows.map(({ row, index }) => {
                          const entered = rowEnteredUnits(row);
                          const variance = entered - row.expectedUnits;
                          const done = row.existingUnits >= row.expectedUnits && row.expectedUnits > 0;
                          return (
                            <tr
                              key={row.id}
                              ref={(element) => { rowRefs.current[row.id] = element; }}
                              className={`border-b border-gray-100 ${
                                index === activeIndex ? 'bg-blue-50/80 ring-2 ring-inset ring-blue-500' : 'bg-white'
                              } ${variance !== 0 && entered > 0 ? 'bg-amber-50' : ''} ${
                                row.status === 'failed' ? 'bg-red-50' : ''
                              }`}
                            >
                              <td className={touchLayout ? 'px-1.5 py-2 font-medium text-gray-700' : 'px-3 py-2 font-medium text-gray-700'}>#{row.boxNumber}</td>
                              <td className={touchLayout ? 'break-words px-1.5 py-2' : 'px-3 py-2'}>
                                <div className="break-words font-medium text-gray-900">{row.sku}</div>
                                <div className="break-words text-sm text-gray-500">{row.productName}</div>
                              </td>
                              <td className={touchLayout ? 'px-1.5 py-2 text-right tabular-nums text-gray-700' : 'px-3 py-2 text-right tabular-nums text-gray-700'}>{row.expectedUnits}</td>
                              <td className={touchLayout ? 'px-1.5 py-2 text-right tabular-nums text-gray-700' : 'px-3 py-2 text-right tabular-nums text-gray-700'}>{row.existingUnits}</td>
                              <td className={touchLayout ? 'px-1.5 py-2 text-right' : 'px-3 py-2 text-right'}>
                                {mode === 'manual' ? (
                                  <input
                                    ref={(element) => { manualInputRefs.current[index] = element; }}
                                    type="number"
                                    min="0"
                                    value={row.inputQty ?? ''}
                                    onFocus={() => setActiveIndex(index)}
                                    onChange={(event) => updateRow(index, { inputQty: event.target.value, epcs: [] })}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        commitManual(index);
                                      }
                                      if (event.key === 'ArrowDown') {
                                        event.preventDefault();
                                        setActiveIndex(Math.min(rows.length - 1, index + 1));
                                      }
                                      if (event.key === 'ArrowUp') {
                                        event.preventDefault();
                                        setActiveIndex(Math.max(0, index - 1));
                                      }
                                    }}
                                    disabled={done && !overrideCompleted}
                                    className="h-8 w-28 rounded-md border border-gray-300 px-2 text-right tabular-nums shadow-inner [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                                  />
                                ) : (
                                  <span className="inline-flex items-center justify-end gap-1 font-mono tabular-nums text-gray-700">
                                    {entered}
                                    {row.epcs?.length > 0 && (
                                      <span
                                        className="text-xs text-blue-600"
                                        title="Entered by scan"
                                        aria-label="Entered by scan"
                                      >
                                        RFID
                                      </span>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td className={touchLayout ? 'break-words px-1.5 py-2' : 'px-3 py-2'}>
                                {row.status === 'success' ? (
                                  <span className="inline-flex items-center gap-1 text-sm font-medium text-green-700">
                                    <CheckCircle2 className="h-4 w-4" /> Applied
                                  </span>
                                ) : row.status === 'failed' ? (
                                  <span className="text-sm text-red-700">{row.error || 'Failed'}</span>
                                ) : done ? (
                                  <span className="text-sm font-medium text-green-700">Done</span>
                                ) : variance !== 0 && entered > 0 ? (
                                  <span className="text-sm font-medium text-amber-700">Variance {variance > 0 ? '+' : ''}{variance}</span>
                                ) : (
                                  <span className="text-sm text-gray-500">Ready</span>
                                )}
                                {row.duplicates?.length > 0 && (
                                  <div className="mt-1 text-sm text-red-700">
                                    Duplicate EPC {row.duplicates[0].hexEpc} — already received in {row.duplicates[0].locationLabel}
                                  </div>
                                )}
                              </td>
                              <td className={touchLayout ? 'px-1.5 py-2 text-right' : 'px-3 py-2 text-right'}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveIndex(index);
                                    updateRow(index, { status: 'skipped', inputQty: '', epcs: [] });
                                    advance(index);
                                  }}
                                  className="text-sm text-gray-500 hover:text-gray-900"
                                >
                                  Skip
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <footer
              className={`flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 ${
                touchLayout ? 'px-2 py-2' : 'px-4 py-3'
              }`}
            >
              <div className="text-sm text-gray-600">
                {applyState?.running
                  ? `Applying ${applyState.completed}/${applyState.total}…`
                  : applyState?.failed
                    ? `${applyState.failed} failed`
                    : 'Nothing is sent until Save & apply.'}
              </div>
              <div className="flex items-center gap-2">
                {applyState?.failed > 0 && (
                  <button
                    type="button"
                    onClick={retryFailed}
                    className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Retry failed
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => applyRows()}
                  disabled={loading || applyState?.running || !rows.some((row) => rowEnteredUnits(row) > 0)}
                  className="rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Save &amp; apply
                </button>
              </div>
            </footer>
          </section>
        </div>
      </main>
      </div>
      <ConfrimModal
        isOpen={!!confirmState}
        title={confirmState?.kind === 'discard' ? 'Discard bulk receiving entries?' : 'Apply over-received boxes?'}
        description={
          confirmState?.kind === 'discard'
            ? 'You have unsaved entries. Discard them and close the workspace?'
            : `The following ${confirmState?.overRows?.length || 0} box(es) exceed expected quantities: ${confirmState?.overRows?.map((row) => `${row.poNumber} #${row.boxNumber}`).join(', ')}.`
        }
        confirmButtonTitle={confirmState?.kind === 'discard' ? 'Discard entries' : 'Apply over-receipt'}
        size="max-w-lg"
        submitClick={() => {
          const kind = confirmState?.kind;
          setConfirmState(null);
          if (kind === 'discard') {
            discardedRef.current = true;
            try {
              window.localStorage.removeItem(draftKey);
            } catch (error) {
              // Ignore unavailable storage.
            }
            onClose();
          }
          else applyRows(true);
        }}
        cancelClick={() => setConfirmState(null)}
      />
    </div>
    ),
    document.body,
  );
}

export default BulkReceiveWorkspace;
